import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import MsTable from "../../../components/Widgets/Table";
import NewArtistReviewModal from "./NewArtistReviewModal";
import PageHeader from "components/PageHeader";
import { colType } from "../../../Types";
import {
  NewArtistsContactSubmission,
  NewArtistStatus,
  NEW_ARTIST_STATUS_META,
  getNewArtistStatusMeta,
  normalizeNewArtistStatus,
  NEW_ARTISTS_SUBMISSIONS_QUERY_KEY,
} from "../../../api/new-artists";
import useNewArtistsContactSubmissionsQuery from "../hooks/useNewArtistsContactSubmissionsQuery";
import { markNewArtistsContactVisited } from "../../../components/hooks/useNewBriefSubmissions";
import DSBox from "../../system-design/DSBox";
import DSAlert from "../../system-design/DSAlert";
import DSChip from "../../system-design/DSChip";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSEnumSelect from "../../system-design/DSEnumSelect";

const ALL_STATUSES = "ALL";
type StatusFilter = NewArtistStatus | typeof ALL_STATUSES;

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: ALL_STATUSES, label: "All statuses" },
  ...Object.values(NewArtistStatus).map((status) => ({
    value: status,
    label: NEW_ARTIST_STATUS_META[status].label,
  })),
];

function NewArtists() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(ALL_STATUSES);
  const {
    data: submissions,
    isLoading,
    isError,
  } = useNewArtistsContactSubmissionsQuery();

  useEffect(() => {
    markNewArtistsContactVisited();
    queryClient.invalidateQueries({
      queryKey: NEW_ARTISTS_SUBMISSIONS_QUERY_KEY,
    });
  }, [queryClient]);

  const columns: Array<colType> = [
    { label: "date", field: "createdAt", sort: true },
    { label: "name", field: "fullName", sort: true },
    { label: "email", field: "email", sort: true },
    { label: "portfolio", field: "portfolioUrl", sort: false },
    { label: "message", field: "message", sort: false },
    { label: "status", field: "status", sort: true },
  ];

  const rows = (submission: NewArtistsContactSubmission) => {
    const portfolio = submission.portfolioUrl || "";
    const safeHref = /^https?:\/\//i.test(portfolio)
      ? portfolio
      : /^[a-z][a-z0-9+.-]*:/i.test(portfolio)
        ? ""
        : `https://${portfolio}`;

    return {
      createdAt: submission.createdAt
        ? new Date(submission.createdAt).toLocaleString()
        : "",
      fullName: submission.fullName,
      email: submission.email,
      portfolioUrl:
        portfolio && safeHref ? (
          <div
            style={{
              width: "100%",
              overflowWrap: "anywhere",
              textAlign: "center",
            }}
          >
            <a
              href={safeHref}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {portfolio}
            </a>
          </div>
        ) : (
          portfolio
        ),
      message: submission.message ? (
        <div
          style={{
            width: "100%",
            maxHeight: 84,
            overflowY: "auto",
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
            textAlign: "left",
          }}
        >
          {submission.message}
        </div>
      ) : (
        ""
      ),
      status: (() => {
        const meta = getNewArtistStatusMeta(submission.reviewDetails?.status);
        return (
          <DSChip
            label={meta.label}
            color={meta.color}
            size="small"
            variant="outlined"
          />
        );
      })(),
    };
  };

  // Surface reviewDetails.status as a top-level field so the "status" column
  // can sort on it (MsTable sorts on the raw row value, not the rendered cell).
  // Memoized so a fresh array identity is only produced when the data or filter
  // actually changes — otherwise MsTable re-runs its sort/filter effect every
  // render.
  const tableData = useMemo(
    () =>
      (submissions ?? [])
        .map((submission) => ({
          ...submission,
          status: normalizeNewArtistStatus(submission.reviewDetails?.status),
        }))
        .filter(
          (submission) =>
            statusFilter === ALL_STATUSES || submission.status === statusFilter,
        ),
    [submissions, statusFilter],
  );

  return (
    <>
      <PageHeader
        overline="Content"
        title="New Artists"
        subtitle="Review contact submissions from the New Artists page."
      />

      {isLoading ? (
        <DSBox sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <DSCircularProgress />
        </DSBox>
      ) : isError ? (
        <DSAlert severity="error">
          Failed to load submissions. Please try again.
        </DSAlert>
      ) : submissions ? (
        <MsTable
          id="NewArtistsContactSubmissions"
          data={tableData}
          cols={columns}
          rowBuild={rows}
          title="New Artists"
          modal={NewArtistReviewModal}
          buttons={
            <DSEnumSelect<StatusFilter>
              value={statusFilter}
              options={STATUS_FILTER_OPTIONS}
              onChange={setStatusFilter}
              size="small"
              sx={{ minWidth: 180 }}
            />
          }
          search={(row: NewArtistsContactSubmission, val: string) =>
            `${row.fullName} ${row.email}`
              .toLowerCase()
              .includes(val.toLowerCase())
          }
        />
      ) : null}
    </>
  );
}

export default NewArtists;
